import styles from "./Sidebar.module.scss";
import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../common/modal/Modal";
import { eraseCookie } from "../../utils/cookies";
import AuthService from "../../api/services/auth.service";
import DashboardIcon from "../../assets/images/sidebar/DashboardIcon";
import AllStores from "../../assets/images/sidebar/AllStores";
import Users from "../../assets/images/sidebar/Users";
import Audit from "../../assets/images/sidebar/Audit";
import Percentage from "../../assets/images/sidebar/Percentage";
import Shipping from "../../assets/images/sidebar/Shipping";
import Currency from "../../assets/images/sidebar/Currency";
import Blling from "../../assets/images/sidebar/Blling";
import Setting from "../../assets/images/sidebar/Setting";
import Categories from "../../assets/images/sidebar/Categories";
import SignOut from "../../assets/images/sidebar/SignOut";
import user from "../../assets/images/Profile.png";
import ProductIcon from "../../assets/images/sidebar/ProductIcon";
import BrandIcon from "../../assets/images/sidebar/BrandIcon";
import AttributesIcon from "../../assets/images/sidebar/AttributesIcon";
import ProfileService from "../../api/services/profile.service";

const LocationIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const ChatIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 5.5C4 4.11929 5.34315 3 7 3H17C18.6569 3 20 4.11929 20 5.5V13.5C20 14.8807 18.6569 16 17 16H9L5 20V5.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="9.5" r="1" fill="currentColor" />
    <circle cx="12" cy="9.5" r="1" fill="currentColor" />
    <circle cx="15" cy="9.5" r="1" fill="currentColor" />
  </svg>
);

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s ease",
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const Sidebar = ({ open, setOpen, collapsed, setCollapsed }: Props) => {
  const { t } = useTranslation("translation");
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string>("Admin");
  const [email, setEmail] = useState<string>("");
  const [logoutModal, setLogoutModal] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false); // Changed from true to false

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await ProfileService.getProfile();
        const data = res.data;
        setProfilePhoto(data.profile_photo || null);
        const name = `${data.first_name || ""} ${data.last_name || ""}`.trim();
        setFullName(name || "Admin");
        setEmail(data.email || "");
      } catch (e) {
        // ignore
      }
    };
    fetchProfile();
  }, []);

  const onSubmit = async () => {
    try {
      eraseCookie("LuxMartAdminDashboardAccessToken");
      AuthService.logout();
    } catch (error: any) {}
  };
  return (
    <>
      {open && (
        <div className={styles.overlay} onClick={() => setOpen(false)} />
      )}
      <aside
        className={`${styles.sidebar} ${open ? styles.open : ""} ${
          collapsed ? styles.collapsed : ""
        }`}
      >
        <div>
          <div className={styles.header}>
            <div className={styles.user_info}>
              <div>
                <img src={profilePhoto || user} alt="" />
              </div>
              {!collapsed && (
                <div>
                  <p className={styles.name}>{fullName}</p>
                  <p className={styles.email}>{email || " "}</p>
                </div>
              )}
            </div>
            <button
              className={styles.collapseBtn}
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? "»" : "«"}
            </button>
            <button className={styles.closeBtn} onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          <nav className={styles.navbar}>
            <ul className={styles.nav_list}>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  end
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <DashboardIcon />
                  {!collapsed && <p>{t("sidebar.dashboard")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/users"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Users />
                  {!collapsed && <p>{t("sidebar.users")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/all-stores"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <AllStores />
                  {!collapsed && <p>{t("sidebar.all_stores")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/stores-percentages"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Percentage />
                  {!collapsed && <p>{t("sidebar.stores_percentages")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/all-shipping"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Shipping />
                  {!collapsed && <p>{t("sidebar.all_shipping")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/payments"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Blling />
                  {!collapsed && <p>{t("sidebar.billing_payments")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/products"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <ProductIcon />
                  {!collapsed && <p>{t("sidebar.products")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/brands"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <BrandIcon />
                  {!collapsed && <p>{t("sidebar.brands")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/categories"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Categories />
                  {!collapsed && <p>{t("sidebar.categories")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/attributes"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <AttributesIcon />
                  {!collapsed && <p>{t("sidebar.attributes")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/currency"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Currency />
                  {!collapsed && <p>{t("sidebar.currency")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/countries-cities"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <LocationIcon />
                  {!collapsed && <p>{t("sidebar.countries_cities")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/messages"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <ChatIcon />
                  {!collapsed && <p>{t("sidebar.messages")}</p>}
                </NavLink>
              </li>
              <li className={styles.list}>
                <div
                  className={`${styles.link} ${styles.dropdownToggle}`}
                  onClick={() => setAuditOpen(!auditOpen)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Audit />
                    {!collapsed && <p>{t("sidebar.audit")}</p>}
                  </div>
                  {!collapsed && <ChevronIcon isOpen={auditOpen} />}
                </div>
                <ul
                  className={`${styles.dropdown} ${
                    auditOpen ? styles.open : ""
                  }`}
                >
                  <li>
                    <NavLink
                      to="/dashboard/audit"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.audit_results")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/dashboard/audit/payout-report"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.payout_reports")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/dashboard/audit/tax-report"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.tax_reports")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/dashboard/audit/store-analytics"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.store_analytics_risks")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/dashboard/audit/settings"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.audit_settings")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/dashboard/audit/installment-banks"
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `${styles.sublink} ${isActive ? styles.active : ""}`
                      }
                    >
                      {t("sidebar.installment_banks")}
                    </NavLink>
                  </li>
                </ul>
              </li>
              <li className={styles.list}>
                <NavLink
                  to="/dashboard/settings"
                  onClick={() => {
                    setOpen(false);
                    setAuditOpen(false);
                  }}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ""}`
                  }
                >
                  <Setting />
                  {!collapsed && <p>{t("sidebar.settings")}</p>}
                </NavLink>
              </li>
              <li className={styles.list} onClick={() => setLogoutModal(true)}>
                <div className={styles.link}>
                  <SignOut />
                  {!collapsed && <p>{t("sidebar.sign_out")}</p>}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
      <Modal
        isOpen={logoutModal}
        onClose={() => setLogoutModal(false)}
        title={t("modal.logout.title") as string}
        onSubmit={onSubmit}
      >
        <div>{t("modal.logout.message") as string}</div>
      </Modal>
    </>
  );
};

export default Sidebar;
